import { Divider, Grid } from '@mui/material';
import {
  HSpacer,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';

import { RouteAvatarName } from '../../../../components/RouteAvatarName';
import { renderInputText } from '../../../../components/right-panel-info/Common';
import { InfoContainer } from '../../../../components/right-panel-info/RightPanel.styled';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { useUpdateProjectGeoFence } from '../../../../hooks/project/useUpdateProjectGeoFence';
import Text from '../../../../react-components/Text';
import { useUpdateDriver } from '../hooks/driver/useUpdateDriver';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../hooks/plan-details/useRetrieveActivePlanStops';
import { PlanGeoFenceStopChip } from './PlanGeoFenceStopChip';

interface PlanGeoFenceBodyProps {
  geoFence: GeoFence;
}

export const PlanGeoFenceBody = ({ geoFence }: PlanGeoFenceBodyProps) => {
  const { data: planDrivers, map: driversMap } = useRetrieveActivePlanDrivers();
  const { data: planStops } = useRetrieveActivePlanStops();
  const { run: updateGeoFence } = useUpdateProjectGeoFence();
  const { run: updateDriver } = useUpdateDriver();

  const handleUpdate = (label: string, val: any) => {
    geoFence.update({ [label]: val });
    updateGeoFence({ geoFence });
  };

  const assignedDrivers = (planDrivers ?? []).filter((driver) => driver.geo_fences?.includes(geoFence.id));

  const assignedStops = (planStops ?? []).filter(
    (stop) =>
      stop.location?.isValid() &&
      geoFence.isMarkerInsideGeoFence({ lat: stop.location!.lat!, lng: stop.location!.lng! })
  );

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ height: `100%`, backgroundColor: RoutalPalette.neutral00 }}
    >
      <InfoContainer>
        {renderInputText({
          labelIntlId: `planner.zones.geo_fence_name`,
          placeholderIntlId: `clients.data.details.label`,
          variant: `outlined`,
          property: `label`,
          size: `title`,
          handleUpdate,
          data: geoFence,
        })}

        <VSpacer medium />

        <Text intlId="planner.zones.linked_vehicles" variant="label" style={{ marginBottom: `8px` }} />

        <RoutalSelect
          id="filter-driver-geo_fences"
          disabled={(planDrivers ?? []).length === 0}
          popoverProps={{
            maxHeight: `unset`,
            options: (planDrivers ?? []).map((driver): RoutalSelectMultiSelectOption => {
              return {
                key: driver.id,
                checked: driver.geo_fences?.includes(geoFence.id),
                hideCheckIcon: false,
                closeOnClick: false,
                onClick: () => {
                  if (driver.geo_fences?.includes(geoFence.id)) {
                    updateDriver({
                      driver: driver,
                      changes: { geo_fences: driver.geo_fences.filter((geo) => geo !== geoFence.id) },
                    });
                  } else {
                    updateDriver({
                      driver: driver,
                      changes: { geo_fences: [...(driver.geo_fences ?? []), geoFence.id] },
                    });
                  }
                },
                content: (
                  <Grid container direction="row" alignItems="center">
                    <Grid item>
                      <RouteAvatarName name={driver.getShortLabel()} color={driver.color?.value} />
                    </Grid>

                    <HSpacer small />

                    <Grid item>
                      {driver.label && driver.label !== `` ? (
                        <RoutalTooltip title={driver.label} key="label">
                          <Text variant="body">
                            {driver.label.length > 40 ? `${driver.label.substring(0, 40)}...` : `${driver.label}`}
                          </Text>
                        </RoutalTooltip>
                      ) : (
                        <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                      )}
                    </Grid>
                  </Grid>
                ),
              };
            }),
          }}
        >
          <Grid container direction="row">
            {assignedDrivers.length === 0 ? (
              <Text variant="label" style={{ cursor: `pointer` }} intlId="planner.zones.no_assigned_vehicles" />
            ) : (
              <Text
                style={{ cursor: `pointer` }}
                intlId="planner.zones.assigned_vehicles"
                intlValues={{ num: assignedDrivers.length }}
              />
            )}
          </Grid>
        </RoutalSelect>
        <VSpacer medium />
      </InfoContainer>

      <Divider />

      <Grid
        item
        container
        direction="column"
        wrap="nowrap"
        style={{ flex: 1, backgroundColor: RoutalPalette.neutral00, overflowY: `hidden` }}
      >
        <Text
          variant="body-large"
          style={{ textAlign: `center`, padding: `12px` }}
          intlId="planner.zones.stops_inside_geo_fence"
          intlValues={{ num: assignedStops.length }}
        />

        <InfoContainer style={{ overflowY: `auto` }}>
          {assignedStops.map((stop) => (
            <PlanGeoFenceStopChip stop={stop} driver={driversMap[stop.driver_id ?? ``]} />
          ))}
        </InfoContainer>
      </Grid>
    </Grid>
  );
};
